/** @module csi/store/GroupNodes */
define([
	"dojo/_base/declare",
	"dojo/promise/all",
	"dojo/when",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/locale",
	"csi/xml",
	"./Nodes"
], function(declare, all, when, csi, Deferred, _, locale, xml, Nodes) {

	/**
	 * Update name for group node based on text and count
	 * @param {Node} node 
	 */
	function updateName(node) {
		if (node._text) {
			node.$Name = locale.resource("ValuePlusCount", [node._text, node._count]);
		}
	}

	var _groupNodes = declare(Nodes, {
		bSorted: true,
		bFiltered: true,
		countProperty: "_count",
		textProperty: "_text",
		refsProperty: "_refs",
		valueProperty: "_value",
		urlProperty: "_url",

		/** @property {boolean} customGrouping - set to true if values from Values store need to grouped in a special way (used in conjunction with onCustomGroupLabel and onCustomGroupValue) */
		//customGrouping: false,

		/**
		 * @property {Store} [defaultValuesStore] - store containing values that must show as groups, even if no instances of that group in the actual data;
		 * the value is identified by the idProperty, and text by the labelProperty
		 */
		//defaultValuesStore: null,

		// Spec: spec for grouped-by attribute
		//spec: null,

		// Nodes: store containing actual nodes being grouped
		//ungroupedStore: null,

		// Request: store used to query for values/nodes which will be converted to group nodes
		// (could be Values, ValuePairs, or Nodes depending on which levels are expanded automatically)
		//valuesStore: null,

		/** @property {Object} _mapValueUrls - map of group values to urls used for client nodes */
		//_mapValueUrls: null,

		/**
		 * @override
		 * If a group node is added to the store dynamically, then reset the values store in case this store is queried again;
		 * otherwise the values store will be missing this new entry
		 */
		add(object, directives) {
			const {valuesStore} = this;

			if (valuesStore) {
				valuesStore.reset();
			}

			return this.inherited(arguments);
		},

		getFullRequest: function() {
			// summary: issue request for full set of urls if previously limited by Fmt.Complete.Max
			console.assert(false);
		},

		getReportRequest: function() {
			// summary: forward report request to main store applying same filters as used on this store
			return this.ungroupedStore.getRequest(this._lastQuery, null, true);
		},

		getResultTotal: function(/*Number?*/nDisplayed) {
			// summary: iterate through results, adding counts along the way
			console.assert(this.isLoaded());

			var urls = this.getResults();
			var n = nDisplayed || urls.length;
			var store = this._store;
			var count = 0;
			var node;

			for (var i = 0; i < n; ++i) {
				if (node = store.get(urls[i])) {
					count += node._count;
				}
			}

			return count;
		},

		/**
		 * Return label for custom group value
		 * @param {any} customGroupValue
		 * @return {string}
		 */
		onCustomGroupLabel: function(customGroupValue) {
			return "";
		},

		/**
		 * Extension point to return custom group value for given value
		 * @param {any} value - unique value for group from node value
		 * @param {Object} item - object from Values store
		 * @return {any}
		 */
		onCustomGroupValue: function(value, item) {
			return value;
		},

		/**
		 * Extension point called when a default group is used
		 * @param {Node} defaultGroupItem - item from default group store
		 * @param {GroupNodes} groupStore
		 * @param {Node} groupNode
		 * @param {Any} groupValue
		 * @param {boolean} bEmpty - indicates if group is currently empty
		 */
		onDefaultGroup: function(defaultGroupItem, groupStore, groupNode, groupValue, bEmpty) { },

		put: function(object, directives) {
			// summary: if adding object to store of different type than original (when reporting), then augment node already in cache with report node
			var old = this.getFromMemory(this.getIdentity(object));

			if (old && (old.$Type != object.$Type)) {
				object = _.extend(old, xml.nodeGetAllAttributes(object));
			}

			updateName(object);

			return this.inherited(arguments);
		},

		/**
		 * Also remove item from valuesStore and defaultValuesStore
		 */
		remove: function(id) {
			var item = this.get(id);
			if (item) {
				var value = item[this.valueProperty];

				if (this.valuesStore) {
					this.valuesStore.remove(value);
				}

				if (this.defaultValuesStore) {
					this.defaultValuesStore.remove(value);
				}
			}

			return this.inherited(arguments);
		},

		/**
		 * Remove reference to result with given id
		 * @param {string} resultId
		 * @return {Object} - return item that references result if found and removed
		 */
		removeRef: function(resultId) {
			const results = this.getAllResults();
			if (!results) {
				return null;
			}

			const {
				countProperty,
				refsProperty,
			} = this;

			return results.find(node => {
				const refs = node[refsProperty];
				const n = _.indexOf(refs, resultId);

				if (n >= 0) {
					refs.splice(n, 1);
					--node[countProperty];
					updateName(node);
					return true;
				}
			});
		},

		_createQueryRequest: function(/*Object?*/query, /*Object?*/options) {
			// summary: ignore options when querying for grouping values, as sorting and page counts will be applied in _doQuery;
			// "query" should include all ad-hoc filters for main store in addition to any group filters if using nested grouping
			if (options) {
				// if requesting more results, then nothing to do; all result nodes are generated on first request
				if (options.start) {
					return null;
				}

				// when creating this store from another store, need to query all rather than just one page
				options = _.omit(options, "count");
			}

			const resultNodes = this.getResultNodes();
			const dfd = new Deferred();

			// track query to values store as it will be used for reporting below
			this._lastQuery = query;

			// if requesting new values, then unload current data
			this._unload();

			const dfds = [this.valuesStore.query(query, options)];

			if (this.defaultValuesStore) {
				dfds.push(this.defaultValuesStore.query());
			}

			when(all(dfds),
				function(results) {
					results.push(resultNodes);
					dfd.resolve(results);
				},
				function(err) {
					dfd.reject(err);
				}
			);

			return dfd;
		},

		_onData: function(/*Object[]*/results, /*Object*/options) {
			// summary: create node for each value returned
			const bCustom = this.customGrouping;
			var data = results[0];
			var defaultValues = results[1];
			var previousNodes = results[2];
			var store = this.valuesStore;
			var defaultStore = this.defaultValuesStore;
			var idCount = store.countProperty;
			var idGroup = store.idProperty;
			var idLabel = store.labelProperty;
			var idRefs = store.refsProperty;
			var typeAttr = this.spec.type;
			var typeNode = this.typeNode;
			var valueUrls = this._mapValueUrls = this._mapValueUrls || {};
			var itemsByValue = {};
			var nodes = [];
			var bRef;
			var textPrefix;

			if (typeAttr == xml.ATTR_REF) {
				bRef = true;
			}

			if (typeAttr == xml.ATTR_BOOLEAN) {
				textPrefix = this.spec.name + ": ";
			} else {
				textPrefix = "";
			}

			// if sorting in descending order by same spec as being grouped by, then reverse order;
			// this is ignored when default values are present as they dictate the order
			if (_.isEmpty(defaultValues)) {
				const sort = _.findWhere(options && options.sort, {attribute: this.spec.queryKey});

				if (sort && sort.descending) {
					data = data.reverse();
				}
			}

			_.each(data, function(item) {
				// when grouping by a ref value, it is tempting to use the ref's url for the group row, but this could potentially
				// cause problems if reporting values on the group row - the real node's attributes may be overwritten by calculated
				// report attributes. If any filtering is applied, then those calculations may not match the real node's values.
				var count = item[idCount];
				var refs = item[idRefs];
				var value = item[idGroup];
				var label = item[idLabel];
				var node;

				if (bCustom) {
					value = this.onCustomGroupValue(value, item);
					label = this.onCustomGroupLabel(value);
					node = itemsByValue[value];
				}

				var url = valueUrls[value];

				if (node) {
					node._count += count;

					// NOTE: There is a general problem with this approach, as sorting could be wrong. This technique works only when
					// the attribute being grouped is the same as the attribute being sorted.
					if (refs) {
						node._refs = node._refs.concat(refs);
					}
				} else {
					node = xml.nodeCreateClientNode(url, typeNode, null, xml.NODE_FLAG_NOEDIT);
					node._value = value;
					node._count = count;
					node._refs = refs;
					nodes.push(node);

					// if grouping by ref, then lookup label from store of default groups which must be provided for this to work
					var defaultItem = bRef && defaultStore?.get(value);
					if (defaultItem) {
						node._text = defaultItem[defaultStore.labelProperty];
					} else {
						node._text = textPrefix + label;
					}

					itemsByValue[value] = node;

					// track client urls used to create nodes for values; try to stay consistent so that table expandState will be preserved
					// when ad-hoc filtering
					if (!url) {
						valueUrls[value] = node.$URL;
					}
				}
			}, this);

			// if default values provided, then ensure that all default values are present
			// since defaultValuesStore is a different store than "data", the items inside can be of different type
			// between these two stores. Keep that in mind for future modifications.
			if (defaultStore) {
				const {urlProperty} = defaultStore;

				// it's possible that new groups have been created, and results have been assigned to those groups;
				// if the values store query did not go to the server to get all new information, it is possible that
				// the default store has the new section, but without knowing the refs that belong to it. In such a case, lookup the
				// refs for this new group from the previous node for the group
				const previousRefsByValue = csi.hashArray(previousNodes, "_value", "_refs");

				idGroup = defaultStore.idProperty;
				idLabel = defaultStore.labelProperty;
				idCount = null;

				nodes = _.map(defaultValues, function(item) {
					var value = item[idGroup];
					var node = itemsByValue[value];
					var bEmpty;

					if (node) {
						itemsByValue[value] = null;
					} else {
						var url = valueUrls[value];

						node = xml.nodeCreateClientNode(url, typeNode, null, xml.NODE_FLAG_NOEDIT);
						node._count = item[idCount] || 0;
						node._refs = item[idRefs] || previousRefsByValue[value] || [];
						node._text = textPrefix + item[idLabel];
						node._value = value;

						// track client urls used to create nodes for values; try to stay consistent so that table expandState will be preserved
						// when ad-hoc filtering
						if (!url) {
							valueUrls[value] = node.$URL;
						}

						bEmpty = true;
					}

					node._url = item[urlProperty];

					// call extension point if so configured
					this.onDefaultGroup(item, this, node, value, bEmpty);

					return node;
				}, this);

				// add any remaining nodes that are not part of the default store
				var remainingNodes = _.compact(_.values(itemsByValue));
				if (remainingNodes.length) {
					nodes = nodes.concat(csi.sort(remainingNodes, this.textProperty));
				}

				// if default store provided, then order of groups is dictated by that store and thus this store is not sorted
				this.bSorted = false;
			}

			this.inherited(arguments, [nodes, options]);
		},

		_onRequestError: function(/*Error*/err) {
			// summary: if error, then unload data
			this._unload();
			this.inherited(arguments);
		},

		/**
		 * Change the name of a group
		 * @param {Object} object - group object
		 * @param {string} name - new name for group
		 */
		rename: function(object, value) {
			object._text = object._value = value;
			return this.put(object);
		},

		_unload: function() {
			// summary: remove data from cache because they are client nodes
			_.each(this.resultUrls, this._store.remove, this._store);
			this.inherited(arguments);
		}
	});

	/**
	 * Create client side only node 
	 * @param {string} url
	 * @param {string} type
	 * @param {string} name
	 * @param {string} text
	 * @param {string} value
	 * @return {Object}
	 */
	_groupNodes.createClientNode = function(url, type, name, text, value) {
		var node = xml.nodeCreateClientNode(url, type, name);
		node._text = text;
		node._count = 0;
		node._refs = [];
		node._value = value;
		return node;
	};

	return _groupNodes;
});